import {
  Person as PersonIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Devices as DevicesIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import { useAuth } from '../contexts/AuthContext';

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    setLoading(true);
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ flex: 1 }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          Settings
        </Typography>

        {/* Account Section */}
        <Paper sx={{ mb: 2 }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText
                primary="Account"
                secondary={user?.email}
              />
            </ListItem>
          </List>
        </Paper>

        {/* Security Section */}
        <Paper sx={{ mb: 2 }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon />
              </ListItemIcon>
              <ListItemText
                primary="Zero-Knowledge Encryption"
                secondary="Your vault is encrypted with your master password"
              />
              <ListItemSecondaryAction>
                <Switch checked disabled />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider component="li" />
            <ListItem
              component="div"
              onClick={() => navigate('/devices')}
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemIcon>
                <DevicesIcon />
              </ListItemIcon>
              <ListItemText
                primary="Trusted Devices"
                secondary="Manage devices that can access your vault"
              />
              <ListItemSecondaryAction>
                <ChevronRightIcon color="action" />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider component="li" />
            <ListItem>
              <ListItemIcon>
                <LockIcon />
              </ListItemIcon>
              <ListItemText
                primary="Change Master Password"
                secondary="Update your vault encryption password"
              />
              <Button variant="outlined" size="small" disabled>
                Coming Soon
              </Button>
            </ListItem>
          </List>
        </Paper>

        {/* Warning */}
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Remember your master password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you forget your master password, your vault cannot be recovered.
            We use zero-knowledge encryption and cannot access your data.
          </Typography>
        </Alert>

        {/* About Section */}
        <Paper sx={{ mb: 2 }}>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText
                primary="About AmnPass"
                secondary="Version 1.0.0"
              />
            </ListItem>
            <Divider component="li" />
            <ListItem>
              <ListItemText
                primary="Security Features"
                secondary={
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li>End-to-end encryption</li>
                    <li>Zero-knowledge architecture</li>
                    <li>Secure key derivation (scrypt)</li>
                    <li>X25519 public-key sharing</li>
                  </Box>
                }
              />
            </ListItem>
          </List>
        </Paper>

        {/* Logout */}
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={() => setLogoutDialogOpen(true)}
          sx={{ mt: 2 }}
        >
          Log Out
        </Button>
      </Container>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle>Log Out?</DialogTitle>
        <DialogContent>
          <Typography>
            You will need to enter your master password to access your vault again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleLogout}
            color="error"
            disabled={loading}
          >
            {loading ? 'Logging out...' : 'Log Out'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

